/* Progressive enhancement: native details and Shopify product forms remain functional. */
if (!customElements.get('tiara-product-details')) {
  customElements.define('tiara-product-details', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('toggle', (event) => {
        const opened = event.target;
        if (this.dataset.singleOpen !== 'true' || !opened.matches('[data-tiara-detail]') || !opened.open) return;
        this.querySelectorAll('[data-tiara-detail]').forEach((detail) => {
          if (detail !== opened) detail.open = false;
        });
      }, { ...options, capture: true });
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        const detail = event.target.closest('[data-tiara-detail]');
        if (detail) detail.open = true;
      }, options);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  });
}

if (!customElements.get('tiara-sticky-purchase')) {
  customElements.define('tiara-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.main = document.getElementById(this.dataset.productSection);
      if (!this.main) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.button = this.querySelector('[data-tiara-submit]');
      this.price = this.querySelector('[data-tiara-price]');
      if (!this.button || !this.price) { this.controller = null; return; }
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      };
      this.scheduleVisibility = () => {
        if (this.visibilityFrame) return;
        this.visibilityFrame = requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.syncVisibility();
        });
      };
      this.button.addEventListener('click', () => {
        const native = this.main.querySelector('.product-form__submit');
        if (!native || native.disabled || native.getAttribute('aria-disabled') === 'true') return;
        const form = native.closest('form');
        // Personalization, selling plans and gift-card fields must stay reachable.
        const needsInput = form && [...form.querySelectorAll('input:not([type="hidden"]), textarea, select')]
          .some((input) => input.name !== 'quantity' && !input.disabled);
        if (needsInput || (form && !form.checkValidity())) {
          native.scrollIntoView({ behavior: 'smooth', block: 'center' });
          form?.reportValidity();
          return;
        }
        native.click(); // Uses the selected variant, native quantity, cart drawer and analytics.
        this.schedule();
      }, options);
      // Keep scroll work limited to visibility, without label/price queries or
      // writes. A jump can cross the entire viewport between IO samples, so a
      // passive, frame-coalesced check is needed even when IO is available.
      window.addEventListener('scroll', this.scheduleVisibility, { ...options, passive: true });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.target === this.nativeButton)) this.scheduleVisibility();
        });
      }
      window.addEventListener('resize', this.schedule, options);
      document.addEventListener('shopify:section:load', () => {
        this.observeOverlays();
        this.schedule();
      }, options);
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.main, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden']
      });
      this.overlayObserver = new MutationObserver(this.schedule);
      this.observeOverlays();
      this.bodyObserver = new MutationObserver(this.schedule);
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.overlayTreeObserver = new MutationObserver((records) => {
        const hasOverlay = (node) => node.nodeType === 1 && (
          node.matches(this.overlaySelector) || node.querySelector(this.overlaySelector)
        );
        if (!records.some((record) => [...record.addedNodes, ...record.removedNodes].some(hasOverlay))) return;
        this.observeOverlays();
        this.schedule();
      });
      this.overlayTreeObserver.observe(document.body, { childList: true, subtree: true });
      this.sync();
    }
    get overlaySelector() {
      return 'cart-drawer, cart-notification, product-modal, dialog, .tiara-p-drawer';
    }
    observeOverlays() {
      this.overlayObserver?.disconnect();
      document.querySelectorAll(this.overlaySelector).forEach((overlay) => {
        this.overlayObserver?.observe(overlay, { attributes: true, attributeFilter: ['class', 'open'] });
      });
    }
    syncVisibility() {
      if (!this.nativeButton) return;
      // Read the current position rather than a possibly queued, older IO entry.
      const passedButton = this.nativeButton.getBoundingClientRect().bottom < 0;
      const modalOpen = passedButton && document.querySelector('cart-drawer.active, cart-notification.active, product-modal[open], dialog[open], .tiara-p-drawer.is-open');
      const hidden = !passedButton || Boolean(modalOpen) || Boolean(this.hadError);
      this.passedButton = passedButton;
      if (this.hidden !== hidden) this.hidden = hidden;
    }
    sync() {
      const native = this.main.querySelector('.product-form__submit');
      if (!native) { this.hidden = true; return; }
      if (this.nativeButton !== native) {
        this.visibilityObserver?.disconnect();
        this.nativeButton = native;
        this.passedButton = native.getBoundingClientRect().bottom < 0;
        this.visibilityObserver?.observe(native);
      } else if (!this.visibilityObserver) {
        this.passedButton = native.getBoundingClientRect().bottom < 0;
      }
      const busy = native.getAttribute('aria-disabled') === 'true' || native.classList.contains('loading');
      const label = native.querySelector('span:not(.loading__spinner)')?.textContent.trim();
      const nativePrice = this.main.querySelector('[id^="price-"] .price');
      const priceNode = nativePrice?.querySelector(nativePrice.classList.contains('price--on-sale')
        ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      const price = priceNode?.textContent.trim();
      // Don't conceal validation or cart errors below the fold.
      const error = this.main.querySelector('.product-form__error-message-wrapper:not([hidden])');
      const hasError = Boolean(error && error.textContent.trim());
      const modalOpen = document.querySelector('cart-drawer.active, cart-notification.active, product-modal[open], dialog[open], .tiara-p-drawer.is-open');
      // Complete DOM/layout reads before writes, and leave unchanged text alone.
      const disabled = native.disabled || busy;
      if (this.button.disabled !== disabled) this.button.disabled = disabled;
      if (this.button.hasAttribute('aria-busy') !== busy) this.button.toggleAttribute('aria-busy', busy);
      const buttonLabel = this.button.querySelector('span');
      if (label && buttonLabel && buttonLabel.textContent !== label) buttonLabel.textContent = label;
      if (priceNode && this.price.textContent !== price) this.price.textContent = price;
      const hidden = !this.passedButton || Boolean(modalOpen) || hasError;
      if (this.hidden !== hidden) this.hidden = hidden;
      if (hasError && !this.hadError) error.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.hadError = hasError;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.overlayObserver?.disconnect();
      this.bodyObserver?.disconnect();
      this.overlayTreeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.visibilityFrame);
      this.controller = null;
      this.frame = null;
      this.visibilityFrame = null;
      this.nativeButton = null;
    }
  });
}
